
import styles from './NewPlanScreen.module.css';
import helpers from '../../helpers.js';
import {useState, useContext, useCallback, useEffect, useRef, useMemo} from 'react';
import { useLocation} from "react-router-dom"; //packages

import { UserContext } from '../../UserContext.js';
import { resolveApiBaseUrl } from '../../services/apiBaseUrl.js';
import { GoogleMap, useJsApiLoader, Polygon, Marker } from '@react-google-maps/api';

import ScreenHeader from "../ScreenHeader.js";
import Module from "../Module.js";
import TextInput from "../TextInput.js";
import Selector from "../../components/Selector";
import Checkbox from '../Checkbox.js';
import KeyFigures from '../KeyFigures.js';
import Overlay from '../Overlay.js';

import Button from "../Button.js";
import FAQ from "../support/FAQ.js";

import mapStyle from '../../assets/MapStyle.js';
import { GOOGLE_MAPS_LOADER_OPTIONS, IS_GOOGLE_MAPS_KEY_MISSING } from '../../config/googleMaps.js';
import iconZoneMarker from '../../assets/images/iconZoneMarker.png';
import iconZoneMarkerUnselected from '../../assets/images/iconZoneMarkerUnselected.png';
import iconGrassland  from '../../assets/images/iconGrassland.png';
import iconArable from '../../assets/images/iconArable.png';

// Grayscale marker icons
import iconMarkerBbbGrey from '../../assets/images/iconMarkerBbbGrey.png';
import iconMarkerWheatGrey from '../../assets/images/iconMarkerWheatGrey.png';
import iconMarkerCowGrey from '../../assets/images/iconMarkerCowGrey.png';
import iconMarkerHousingGrey from '../../assets/images/iconMarkerHousingGrey.png';

import ToggleSwitch from '../ToggleSwitch.js';

// Helper function to get grayscale marker icon
const getGrayscaleMarkerIcon = (markerType) => {
  const grayscaleIconMap = {
    'bbb': iconMarkerBbbGrey,
    'wheat': iconMarkerWheatGrey,
    'cow': iconMarkerCowGrey,
    'dairy': iconMarkerCowGrey,
    'housing': iconMarkerHousingGrey,
  };
  return grayscaleIconMap[markerType] || iconMarkerBbbGrey;
};

const mapContainerStyle = {
  width: '100%',
  height: '500px',
  marginTop: '3px',
  boxShadow: "0.2rem 0.2rem 0.5rem rgba(0, 0, 0, 0.1), 0px 4px 0.6rem rgba(0, 0, 0, 0.12)",
  borderRadius: "1.3rem"
};


//54.713801, -2.749613
const center = {
  lat: 54.713801,
  lng: -2.749613
};

const zonesArray = [
  {
    id: "",
    status: "delivered",
    deliveryDate: "",
    pincharBags: "",
    pelletcharBags: "",
    weight: "",
    volume: ""
  }
]

const yearsAvailable = [
  { name: "2025", value: "2025" },
  { name: "2026", value: "2026" },
  { name: "2027", value: "2027" },
  { name: "2028", value: "2028" },
  { name: "2029", value: "2029" }
]

const fieldTypeData = [
  { name: "Arable", value: "arable" },
  { name: "Grassland", value: "grassland" },
  { name: "Woodland", value: "woodland" },
  { name: "Other", value: "other" }
];

const BEDDING_OPTIONS = [
  { name: "Straw pellets", value: "wheat_straw_pellets" },
  { name: "Wood shavings", value: "wood_shavings" },
  { name: "Other", value: "other" },
];

const BAG_TONNES_BY_PRODUCT = {
  pinchar: 0.215,     // 215 kg/bag
  pelletchar: 0.65,   // 0.65 t/bag
};




// near top
const PLAN_TYPE_OPTIONS = [
  { name: "Land Application", value: "land" },
  { name: "Livestock Housing", value: "livestock" },
];

// bags math
const BAG_KG = 215;
const BAGS_PER_LORRY = 24;




function NewPlanScreen(props) {
 
  const { isLoaded, loadError } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

const {user, setUser} = useContext(UserContext);
const [map, setMap] = useState(null);
const [allPolygons, setAllPolygons] = useState([]);
const [zones, setZones] = useState([]);
const [planType, setPlanType] = useState("land"); // "land" | "livestock"

const visibleZones = useMemo(
  () => zones.filter(z => (planType === "land" ? z.zone_type === "land" : z.zone_type === "livestock")),
  [zones, planType]
);

const [selectedZones, setSelectedZones] = useState([]);

const [drawZoneBoundary, setDrawZoneBoundary] = useState(true);// IF TRUE THEN DRAWING POLYGON RATHER THAN SETTING A CENTER POINT
const [zoneGeometry, setZoneGeometry] = useState({});
const markerRef = useRef(null); // Store single marker reference
const polygonRef = useRef(null);
const [existingZones, setExistingZones] = useState([]); // 🔹 Store previous zones from account
const [displaySuccessOverlay, setDisplaySuccessOverlay] = useState(false);
const [deliveryDetails, setDeliveryDetails] = useState({});
const [billingDetails, setBillingDetails] = useState({});

// land: you already have targetedRates = { [zoneId]: t/ha }
const [livestockInputs, setLivestockInputs] = useState({
  cycles: 1,                                      // global cycles per plan (screenshot)
  perZone: {}                                     // { [zoneId]: { initialT, topupT, inclusionPct } }
});
// optional: product/bedding inputs shown in UI, not yet sent to API
const [product, setProduct] = useState("");
const [beddingType, setBeddingType] = useState("");
const [beddingOther, setBeddingOther] = useState("");


const [formData, setFormData] = useState({
  planName: '',
  planYear: '',
  targetedRates: {} // 👈 Map zone IDs to targeted rates
});
const location = useLocation();
const params = location.state || {};



useEffect(() => {
  // select first visible zone (if any)
  setSelectedZones(visibleZones.length ? ["0"] : []);

  if (planType === "land") {
    // set default targeted rates for land zones
    setFormData(prev => ({ ...prev, targetedRates: getDefaultTargetedRates(visibleZones) }));
  } else {
    // set default livestock per-zone inputs
    const perZone = {};
    visibleZones.forEach(z => {
      perZone[z._id] = { initialT: 2, topupT: 1, inclusionPct: 7.2 };
    });
    setLivestockInputs(prev => ({ ...prev, perZone }));
  }
}, [planType, zones]); // runs whenever type or zones list changes


const handleChange = (formField, value) => {
  setFormData((prevData) => ({
    ...prevData,
    [formField]: formField === "surfaceArea" && value !== "" && isNaN(value) 
      ? prevData.surfaceArea // Keep previous valid value if input is invalid
      : value, // Otherwise, store new value
  }));
};

const handleTargetedRateChange = (zoneId, value) => {
  setFormData((prev) => ({
    ...prev,
    targetedRates: {
      ...prev.targetedRates,
      [zoneId]: value
    }
  }));
};

const getDefaultTargetedRates = (zones) => {
  const rates = {
    grassland: 500,
    arable: 1000 ,
    other: 1000
  };

  const targeted = {};
  zones.forEach((zone) => {
    const type = zone.field_type?.toLowerCase();
    if (type && rates[type]) {
      targeted[zone._id] = (rates[type] / 1000).toFixed(2); // convert to t/ha
    }
  });
  return targeted;
};


const onLoad = useCallback((mapInstance) => {
  setMap(mapInstance);

  setTimeout(() => {
    const incomingZones = params.zones || [];

    setZones(incomingZones);
    const firstIndicesOfType = incomingZones
      .map((z, i) => ({ z, i }))
      .filter(({ z }) => (planType === "land" ? z.zone_type === "land" : z.zone_type === "livestock"))
      .map(({ i }) => i.toString());
    setSelectedZones(firstIndicesOfType.length ? [firstIndicesOfType[0]] : []);

    const defaultRates = getDefaultTargetedRates(incomingZones);
    setFormData((prev) => ({
      ...prev,
      targetedRates: defaultRates
    }));

    if (mapInstance && incomingZones.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      incomingZones.forEach(zone => {
        if (zone.center_point?.coordinates?.length === 2) {
          bounds.extend({
            lat: zone.center_point.coordinates[1],
            lng: zone.center_point.coordinates[0]
          });
        }
        if (zone.polygon_shape?.coordinates?.length > 0) {
          zone.polygon_shape.coordinates[0].forEach(coord => {
            bounds.extend({ lat: coord[1], lng: coord[0] });
          });
        }
      });
      mapInstance.fitBounds(bounds);
    }
  }, 100);
}, []);

const onUnmount = useCallback((mapInstance) => {
  setMap(null);
}, []);


const handleSubmit = async () => {
  try {
    
    if(!formData.planName?.length > 0){
      alert("Please add a plan name");
      return;
    }

    if(!formData.planYear?.length > 0 && planType === "land"){
      alert("Please add a target year");
      return;
    }

    if(!beddingType?.length > 0 && planType === "livestock"){
      alert("Please select a bedding type");
      return;
    }

    // const hasMissingTargetedRates = () => {
    //   return selectedZones.some((index) => {
    //     const zone = zones[parseInt(index)];
    //     const rate = formData.targetedRates[zone._id];
    //     return rate === undefined || rate === "" || isNaN(rate);
    //   });
    // };

    const hasMissingTargetedRates = () => {
      if (planType === "livestock") return false; // not used for livestock UI
      return selectedZones.some((index) => {
        const zone = visibleZones[parseInt(index)];
        if (!zone) return true;
        const rate = formData.targetedRates[zone._id];
        return rate === undefined || rate === "" || isNaN(rate);
      });
  };

    if (hasMissingTargetedRates()) {
      alert("Please enter a targeted rate for all selected zones.");
      return;
    }


   
    // const requestBody = {
    //   _user: user.details.id,
    //   plan_name: formData.planName,
    //   plan_year: formData.planYear,
    //   targeted_zones: selectedZones.map((index) => {
    //     const zone = zones[parseInt(index)];
    //     const targetedRate = parseFloat(formData.targetedRates[zone._id]) || 0;

    //     if (zone.zone_type === "land") {
    //       return {
    //         _zone: zone._id,
    //         zone_type: "land",
    //         area: zone.surface_area,
    //         targeted_rate: targetedRate
    //       };
    //     } else if (zone.zone_type === "livestock") {
    //       return {
    //         _zone: zone._id,
    //         zone_type: "livestock",
    //         total_shed_area: zone.livestock_housing?.total_shed_area || 0,
    //         targeted_rate_per_sqm: targetedRate,
    //         cycles_per_year: zone.livestock_housing?.cycles_per_year || 1
    //       };
    //     }
    //   })

    // };
    console.log(product)
    const requestBody = {
      _user: user.details.id,
      plan_name: formData.planName,
      plan_type: planType,
      product_type: product || "pinchar",
      ...(planType === "land" ? { plan_year: Number(formData.planYear) } : {}),
      ...(planType === "livestock" ? { bedding_type: beddingType } : {}),
      targeted_zones: selectedZones.map((index) => {
        const zone = visibleZones[parseInt(index)];

        if (planType === "land") {
          return {
            _zone: zone._id,
            zone_type: "land",
            area: zone.surface_area,
            targeted_rate: parseFloat(formData.targetedRates[zone._id]) || 0, // t/ha
          };
        } else {
          const inp = livestockInputs.perZone[zone._id] || {};
          const shed = Number(zone.livestock_housing?.total_shed_area || 0);
          const init = Number(inp.initialT || 0);
          const top  = Number(inp.topupT || 0);
          const inc  = Number(inp.inclusionPct || 0) / 100;
          const cycles = Number(livestockInputs.cycles || 1);

       // ✅ Correct: compute tonnes per cycle, then per year, THEN divide by area
    const biocharPerCycleT = (init + top) * inc;        // t per cycle
    const targeted_rate_per_sqm = shed > 0
     ? (biocharPerCycleT * 1000) / shed                // ✅ kg/m² per cycle
       : 0;
          return {
            _zone: zone._id,
            zone_type: "livestock",
            total_shed_area: shed,
            targeted_rate_per_sqm,     // kg/m² (annualized)
            cycles_per_year: cycles,
          };
        }
      }),
    };

    console.log("REQUEST BODY")
    console.log(requestBody)

    // // 🔹 Construct request body to match backend schema
    // const requestBody = {
    //   _user: user.details.id,
    //   zone_name: formData.zoneName,
    //   field_name: formData.fieldName || formData.zoneName, // Default to zone name if empty
    //   field_id: formData.fieldId,
    //   center_point: finalCenterPoint, // Either manually_set or extrapolated
    //   polygon_shape: finalPolygonShape || null, // Send null if empty
    //   surface_area: formData.surfaceArea || null,
    //   soil_type: formData.soilType || null,
    //   field_type: formData.fieldType || null,
    //   cultivated_in_next_3_years: formData.isCultivating
    // };

    const request = {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: "Bearer " + user.token,
      },
      body: JSON.stringify(requestBody),
    };

    // Send request
    const response = await fetch(user.backEndURL + "/plans", request);
    const jsonResponse = await response.json();

    if (jsonResponse.success) {
      console.log(jsonResponse.data);
      setDisplaySuccessOverlay(true);
      const fetchAddressDetails = async () => {

      let user = {};
      user.backEndURL = resolveApiBaseUrl();
      user.token = localStorage.getItem("token");

      if (user.token === null){
        console.log("NO TOKEN")
        user.authed = false;
      } else {
        const request = {
          method: "get",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + user.token,
          }
        };
    
        let response = await fetch(user.backEndURL + "/fetch-address-details", request);
        let jsonResponse = await response.json();
        if (jsonResponse.success) {
          console.log(jsonResponse.data)
          setDeliveryDetails({
            firstName: jsonResponse.data.deliveryDetails.first_name,
            lastName: jsonResponse.data.deliveryDetails.last_name,
            phone: jsonResponse.data.deliveryDetails.phone,
            email: jsonResponse.data.deliveryDetails.email,
            businessName: jsonResponse.data.deliveryDetails.business_name,
            addressLine1: jsonResponse.data.deliveryDetails.address_line_1,
            addressLine2: jsonResponse.data.deliveryDetails.address_line_2,
            city: jsonResponse.data.deliveryDetails.city,
            zip: jsonResponse.data.deliveryDetails.zip,
            county: jsonResponse.data.deliveryDetails.county,
            instructions: jsonResponse.data.deliveryDetails.instructions
          });
          setBillingDetails({
            firstName: jsonResponse.data.billingDetails.first_name,
            lastName: jsonResponse.data.billingDetails.last_name,
            phone: jsonResponse.data.billingDetails.phone,
            email: jsonResponse.data.billingDetails.email,
            businessName: jsonResponse.data.billingDetails.business_name,
            addressLine1: jsonResponse.data.billingDetails.address_line_1,
            addressLine2: jsonResponse.data.billingDetails.address_line_2,
            city: jsonResponse.data.billingDetails.city,
            zip: jsonResponse.data.billingDetails.zip,
            county: jsonResponse.data.billingDetails.county
          });
          console.log("SILENTLY COMPLETES ITS MISSION, LIKE A NINJA");
        } else {
          console.log("Fetch Address Details Failed...")
        }
      }
    }
    fetchAddressDetails();
      // props.goToScreen("zones"); // Redirect on success
    } else {
      alert("Something went wrong with request" );
      // helpers.errorPrompt("Oops! Something went wrong while creating the zone.", null, setPrompt, null);
    }
  } catch (err) {
    alert("Error creating zone:", err);

    console.error("Error creating zone:", err);
    // helpers.errorPrompt("Unexpected error occurred. Please try again.", null, setPrompt, null);
  }
};

// const toggleZoneSelection = (index) => {
//   const zoneIndex = index.toString();
//   setSelectedZones((prev) =>
//     prev.includes(zoneIndex)
//       ? prev.filter((z) => z !== zoneIndex)
//       : [...prev, zoneIndex]
//   );
// };

const toggleZoneSelection = (index) => {
  const zoneIndex = index.toString();
  setSelectedZones(prev => prev.includes(zoneIndex)
    ? prev.filter(z => z !== zoneIndex)
    : [...prev, zoneIndex]);
};


const updateLivestockZone = (zoneId, key, value) => {
  setLivestockInputs(prev => ({
    ...prev,
    perZone: {
      ...prev.perZone,
      [zoneId]: { ...(prev.perZone[zoneId] || {}), [key]: value }
    }
  }));
};


const calculateRecommendedBiocharRate = (zone) => {
  const { field_type, surface_area } = zone;

  if (!field_type || !surface_area || isNaN(surface_area)) {
    return null; // Invalid input
  }

  // kg/ha rates
  const rates = {
    grassland: 500,
    arable: 1000,
    other: 1000
  };

  const rateKgPerHa = rates[field_type.toLowerCase()];
  if (!rateKgPerHa) {
    return null; // Unsupported field type
  }

  const totalKg = surface_area * rateKgPerHa;
  const totalTons = totalKg / 1000;

  return {
    total:  totalTons.toFixed(2), // Round to 2 decimal places
    rate: (rateKgPerHa / 1000).toFixed(2) // Convert to tons/ha and round to 2 decimal places
  }
}


const renderLandRows = () => {
  const filtered = visibleZones.filter((_, idx) => selectedZones.includes(idx.toString()));
  return filtered.map((zone, index) => {
    const rec = calculateRecommendedBiocharRate(zone); // your existing helper
    return (
      <tr key={zone._id} className={styles.tableRow}>
        <td className={helpers.clx(styles.zoneRowName, styles.dataBold)}>{zone.zone_name}</td>
        <td className={styles.dataBold}>{zone.surface_area ?? "-"}</td>
        <td className={styles.dataBold}>{!zone.applications || zone.applications.length === 0 ? "0" : "n/a"}</td>
        <td className={styles.dataBold}>{rec?.rate ?? "-"}</td>
        <td className={styles.dataInput}>
          <input
            type="number"
            step="0.01"
            value={formData.targetedRates[zone._id] ?? ""}
            onChange={(e) => handleTargetedRateChange(zone._id, e.target.value)}
            className={styles.dataInputText}
          />
        </td>
        <div className={styles.zoneRowRemoveButton}
             onClick={() => setSelectedZones(selectedZones.filter(z => z !== zones.indexOf(zone).toString()))}>
          <span className={styles.zoneRowRemoveIcon}>&#xf057;</span>
        </div>
      </tr>
    );
  });
};

const renderLivestockRows = () => {
  const filtered = visibleZones.filter((_, idx) => selectedZones.includes(idx.toString()));

  return filtered.map((zone) => {
    const z = livestockInputs.perZone[zone._id] || { initialT: "", topupT: "", inclusionPct: "" };

    const init = Number(z.initialT || 0);
    const top  = Number(z.topupT || 0);
    const inc  = Number(z.inclusionPct || 0) / 100;

    const totalT = (init + top) * inc; // ✅ compute numeric total
    const currentIdx = visibleZones.findIndex(vz => vz._id === zone._id);

    return (
      <tr key={zone._id} className={styles.tableRow}>
        <td className={helpers.clx(styles.zoneRowName, styles.dataBold)}>{zone.zone_name}</td>

        <td className={styles.dataInput}>
          <input
            type="number" step="0.01" value={z.initialT}
            onChange={(e) => updateLivestockZone(zone._id, "initialT", e.target.value)}
            className={styles.dataInputText}
          />
        </td>

        <td className={styles.dataInput}>
          <input
            type="number" step="0.01" value={z.topupT}
            onChange={(e) => updateLivestockZone(zone._id, "topupT", e.target.value)}
            className={styles.dataInputText}
          />
        </td>

        <td className={styles.dataInput}>
          <input
            type="number" step="0.1" value={z.inclusionPct}
            onChange={(e) => updateLivestockZone(zone._id, "inclusionPct", e.target.value)}
            className={styles.dataInputText}
          />
        </td>

        <td className={styles.dataBold}>{Number.isFinite(totalT) ? totalT.toFixed(2) : "0.00"}</td>

        <div
          className={styles.zoneRowRemoveButton}
          onClick={() => setSelectedZones(selectedZones.filter(zI => zI !== currentIdx.toString()))}
        >
          <span className={styles.zoneRowRemoveIcon}>&#xf057;</span>
        </div>
      </tr>
    );
  });
};



// Helper function to calculate bag quantities for the plan
const calculatePlanBagQuantities = () => {
  const selected = selectedZones
    .map(index => visibleZones[parseInt(index)])
    .filter(Boolean);

  if (planType === "land") {
    // Land application calculation
    const totalTargetKg = selected.reduce((sum, z) => {
      const rate = parseFloat(formData.targetedRates[z._id] || 0); // t/ha
      return sum + rate * parseFloat(z.surface_area || 0) * 1000;
    }, 0);
    
    // Calculate bags based on selected product
    const bagTonnes = BAG_TONNES_BY_PRODUCT[product] || BAG_TONNES_BY_PRODUCT.pinchar;
    const totalBags = Math.ceil(totalTargetKg / (bagTonnes * 1000)); // Convert tonnes to kg
    
    return {
      pincharBags: product === "pinchar" ? totalBags : 0,
      pelletcharBags: product === "pelletchar" ? totalBags : 0,
      productType: product || "pinchar"
    };
  } else {
    // Livestock application calculation
    const cycles = Number(livestockInputs.cycles || 1);
    let totalBiocharT = 0;

    selected.forEach(z => {
      const inp = livestockInputs.perZone[z._id] || {};
      const init = Number(inp.initialT || 0);
      const top  = Number(inp.topupT || 0);
      const inc  = Number(inp.inclusionPct || 0) / 100;

      const beddingPerCycleT = init + top;
      const biocharPerCycleT = beddingPerCycleT * inc;
      totalBiocharT += biocharPerCycleT;
    });

    totalBiocharT *= cycles;

    // Calculate bags based on selected product
    const bagTonnes = BAG_TONNES_BY_PRODUCT[product] || BAG_TONNES_BY_PRODUCT.pinchar;
    const totalBags = Math.ceil(totalBiocharT / bagTonnes);
    
    return {
      pincharBags: product === "pinchar" ? totalBags : 0,
      pelletcharBags: product === "pelletchar" ? totalBags : 0,
      productType: product || "pinchar"
    };
  }
};

const handleProductChange = (newProduct) => {
  setProduct(newProduct);
  if (planType === "land") {
    // Recalculate bag quantities for land application
    const bagQuantities = calculatePlanBagQuantities();
    // Update relevant state to reflect new calculations
    setFormData(prevData => ({ ...prevData, bagQuantities }));
  }
};

const renderSuccessOverlay = () => {
  if (!displaySuccessOverlay) return null;

  const bagQuantities = calculatePlanBagQuantities();

  return (
    <Overlay>
      <div className={styles.successOverlayContainer}>
        <h2>Plan Successfully Created!</h2>
        <p>Your action plan <b>{formData.planName}</b> for {formData.planYear} has been successfully created.</p>
        <div className={styles.overlayButtons}>
          <Button name="Back to Zones" color="Coal" onPress={() => props.goToScreen("zones")} />
          <Button name="Set Up Order" color="Leaf" onPress={() => props.goToScreen("update-subscription", {
            addresses: {deliveryDetails: deliveryDetails, billingDetails: billingDetails},
            planData: {
              bagQuantities: bagQuantities,
              planType: planType,
              planName: formData.planName,
              planYear: formData.planYear
            }
          })} />
        </div>
      </div>
    </Overlay>
  );
};

// const renderBreakdown = () => {
//   if (!formData || !zones || zones.length === 0) return null;

//   const selected = selectedZones
//     .map(index => zones[parseInt(index)])
//     .filter(Boolean);

//   const totalArea = selected.reduce((sum, zone) => {
//     return sum + parseFloat(zone.surface_area || 0);
//   }, 0);

//   const totalTargetKg = selected.reduce((sum, zone) => {
//     const rate = parseFloat(formData.targetedRates[zone._id] || 0); // ⬅️ FIXED
//     return sum + rate * parseFloat(zone.surface_area || 0) * 1000;
//   }, 0);

//   const avgTargetRate = totalArea > 0 ? (totalTargetKg / totalArea / 1000).toFixed(2) : "0";
//   const totalBags = Math.ceil(totalTargetKg / 215);
//   const totalLorries = Math.round((totalBags  )/ 2.4)/10;

//  return <KeyFigures data={[
//     {label: "Total Area", value: totalArea.toFixed(2), unit: "ha" },
//     {label: "Avg. Target Rate", value: avgTargetRate, unit: "t/ha" },
//     {label: "Bags Equiv.", value: totalBags, unit: "bags" },
//     {label: "Artic Loads", value: totalLorries}
//  ]}/>
 

// //  return (
// //   <div className={styles.breakdownRow}>
// //     <div className={styles.breakdownItem}>
// //       <div className={styles.breakdownLabel}>Total Area</div>
// //       <div className={styles.breakdownValue}>{totalArea.toFixed(2)} <span className={styles.unit}>ha</span></div>
// //     </div>
// //     <div className={styles.separatorLine}/>

// //     <div className={styles.breakdownItem}>
// //       <div className={styles.breakdownLabel}>Avg. Target Rate</div>
// //       <div className={styles.breakdownValue}>{avgTargetRate} <span className={styles.unit}>t/ha</span></div>
// //     </div>
// //     <div className={styles.separatorLine}/>

// //     <div className={styles.breakdownItem}>
// //       <div className={styles.breakdownLabel}>Bags Equiv.</div>
// //       <div className={styles.breakdownValue}>{totalBags} <span className={styles.unit}>bags</span></div>
// //     </div>
// //     <div className={styles.separatorLine}/>

// //     <div className={styles.breakdownItem}>
// //       <div className={styles.breakdownLabel}>Artic Loads</div>
// //       <div className={styles.breakdownValue}>~ {totalLorries}</div>
// //     </div>
// //   </div>
// // );
// };

const renderBreakdown = () => {
  const selected = selectedZones
    .map(index => visibleZones[parseInt(index)])
    .filter(Boolean);

  if (planType === "land") {
    const totalArea = selected.reduce((s, z) => s + (parseFloat(z.surface_area || 0)), 0);
    const totalTargetKg = selected.reduce((sum, z) => {
      const rate = parseFloat(formData.targetedRates[z._id] || 0); // t/ha
      return sum + rate * parseFloat(z.surface_area || 0) * 1000;
    }, 0);
    const avgTargetRate = totalArea > 0 ? (totalTargetKg / totalArea / 1000).toFixed(2) : "0.00";
    
    // Use product-specific bag weight instead of hardcoded BAG_KG
    const bagTonnes = BAG_TONNES_BY_PRODUCT[product] || BAG_TONNES_BY_PRODUCT.pinchar;
    const bagKg = bagTonnes * 1000; // Convert tonnes to kg
    const totalBags = Math.ceil(totalTargetKg / bagKg);
    const totalLorries = Math.ceil(totalBags / BAGS_PER_LORRY);

    return <KeyFigures data={[
      { label: "Total Area", value: totalArea.toFixed(2), unit: "ha" },
      { label: "Avg. Target Rate", value: avgTargetRate, unit: "t/ha" },
      { label: "Bags Equiv.", value: totalBags, unit: "bags" },
      { label: "Artic Loads", value: totalLorries }
    ]}/>;
  }

  // LIVESTOCK
    const cycles = Number(livestockInputs.cycles || 1);

    // Sum per-row numbers (same logic as the row) and then apply cycles
    let totalBeddingT = 0;   // bedding tonnage before inclusion
    let totalBiocharT = 0;   // biochar tonnage after inclusion

    selected.forEach(z => {
      const inp = livestockInputs.perZone[z._id] || {};
      const init = Number(inp.initialT || 0);
      const top  = Number(inp.topupT || 0);
      const inc  = Number(inp.inclusionPct || 0) / 100;

      const beddingPerCycleT = init + top;           // ❌ no shed multiplier here
      const biocharPerCycleT = beddingPerCycleT * inc;

      totalBeddingT += beddingPerCycleT;
      totalBiocharT += biocharPerCycleT;
    });

    totalBeddingT *= cycles;
    totalBiocharT *= cycles;

    // bags: depend on product
    const bagTonnes = BAG_TONNES_BY_PRODUCT[product] || BAG_TONNES_BY_PRODUCT.pinchar;
    const totalBags  = Math.ceil(totalBiocharT / bagTonnes);
    const totalLorries = Math.ceil(totalBags / BAGS_PER_LORRY);

    return <KeyFigures data={[
      { label: "Total Bedding", value: totalBeddingT.toFixed(2), unit: "t" },
      { label: "Total Biochar", value: totalBiocharT.toFixed(2), unit: "t" },
      { label: "Bags Equiv.",   value: totalBags, unit: "bags" }
    ]}/>;

};


return(
  <div className={helpers.clx("mainWhiteContainerContent", props.screenTransitioning ? "mainWhiteContainerContentHidden" : "")}>
    {renderSuccessOverlay()}
      <ScreenHeader name={"New Action Plan"} content={"Create a new action plan to apply biochar to your preferred zones. Select the plan type* and application rates based on your needs."}/>
      
      <div className={helpers.clx("contentGrid", styles.contentGrid)}>
        {/* <div className={styles.newZoneButtonContainer}>
          <Button name={"Add New Zone +"} onPress={() => props.goToScreen("/new-zone")} color={"Coal"}/>
        </div> */}
        <Module icon={'\uf648'} name={"Set Plan Targets"} spanColumn={24} height={"100%"}>
          
          { (loadError || IS_GOOGLE_MAPS_KEY_MISSING) ? (
            <div style={{ ...mapContainerStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '1rem' }}>
              The map couldn’t be loaded. Please refresh the page or try again shortly.
            </div>
          ) : isLoaded ?
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={8}
              onLoad={onLoad}
              onUnmount={onUnmount}
              options={{
                styles: mapStyle,
                disableDefaultUI: true,
                draggable: true,
                zoomControl: true,
                scrollwheel: true,
                disableDoubleClickZoom: true,
                draggableCursor: "crosshair",
                mapTypeId: "hybrid",
                keyboardShortcuts: false, // disables keyboard shortcuts AND hides the button
              }}
            >
               
              {/* Render existing zones */}
              {visibleZones.map((zone, index) => {
                if (zone.polygon_shape?.coordinates?.length > 0) {
                  // Convert GeoJSON format to Google Maps Polygon format
                  const polygonPaths = zone.polygon_shape.coordinates[0].map(coord => ({
                    lat: coord[1],  // GeoJSON stores [lng, lat], Google Maps uses {lat, lng}
                    lng: coord[0]
                  }));
                  const isSelected = selectedZones.includes(index.toString());

                  return (
                    <>
                      {/* Polygon */}
                      <Polygon
                        key={`polygon-${zone._id}`}
                        paths={polygonPaths}
                        options={{
                          fillColor:  isSelected ? "#B0E000" : "#FFFFFF", // ✅ Your requested color
                          fillOpacity: isSelected ? 0.25 : 0.1, // 🔹 Adjust opacity for unselected
                          strokeColor: isSelected ? "#B0E000" : "#CCCCCC",
                          strokeOpacity: 0.5, 
                          strokeWeight: 2,
                          clickable: true,
                          editable: false,
                          draggable: false,
                        }}
                        onClick={() => toggleZoneSelection(index)} // ✅ ADD THIS
                      />
                      
                      {/* Polygon Label */}
                      <Marker
                        key={`polygon-label-${zone._id}`}
                        position={polygonPaths[0]} // First coordinate as label anchor
                        icon={{
                          url: "", // ✅ Hide default icon
                          scaledSize: new window.google.maps.Size(1, 1),
                          labelOrigin: new window.google.maps.Point(1, 15) // ✅ Adjust label position
                        }}
                        label={{
                          text: zone.zone_name, // ✅ Display the zone name
                          color: "white",
                          fontSize: "12px",
                          fontWeight: "bold",
                          className: styles.polygonLabel
                        }}
                        onClick={() => toggleZoneSelection(index)} // ✅ ADD THIS
                      />
                    </>
                  );
                } else if (zone.center_point?.coordinates?.length === 2) {
                  const isSelected = selectedZones.includes(index.toString());
                  return (
                    <>
                      {/* Marker */}
                      <Marker
                        key={`marker-${zone._id}`}
                        position={{
                          lat: zone.center_point.coordinates[1], 
                          lng: zone.center_point.coordinates[0],
                        }}
                        icon={{
                          url: isSelected 
                            ? helpers.getMarkerIcon(zone.marker_type) 
                            : getGrayscaleMarkerIcon(zone.marker_type),
                          scaledSize: isSelected
                            ? new window.google.maps.Size(100, 100)  // ✅ Bigger for selected zones
                            : new window.google.maps.Size(65, 65),    // ✅ Normal size for unselected
                          anchor: isSelected
                            ? new window.google.maps.Point(50, 100)   // ✅ Adjust anchor for bigger size
                            : new window.google.maps.Point(32.5, 65),  // ✅ Normal anchor
                          labelOrigin: isSelected
                            ? new window.google.maps.Point(50, 115)   // ✅ Adjust label position for bigger size
                            : new window.google.maps.Point(32.5, 80)   // ✅ Normal label position
                        }}
                        options={{
                          opacity: isSelected ? 1 : 0.8 // Reduced opacity for unselected zones
                        }}
                        label={{
                          text: zone.zone_name,
                          color: "white",
                          fontSize: "12px",
                          fontWeight: "bold",
                          className: styles.markerLabel
                        }}
                        onClick={() => toggleZoneSelection(index)} // ✅ ADD THIS
                      />
                    </>
                  );
                }
                return null;
              })}
            </GoogleMap>
          : null
          }
          <div className={styles.contentWrapper}>
            <Selector
              name="Plan Type*"
              value={planType}
              onChange={(v) => {
                setPlanType(v);
                // reset selected zones to first of the newly visible list
                const indices = zones
                  .map((z, i) => ({ z, i }))
                  .filter(({ z }) => (v === "land" ? z.zone_type === "land" : z.zone_type === "livestock"))
                  .map(({ i }) => i.toString());
                setSelectedZones(indices.length ? [indices[0]] : []);
              }}
              labelStyle="top"
              data={[
                { name: "Land Application", value: "land" },
                { name: "Livestock Housing", value: "livestock" },
              ]}
            />

            <div className={styles.targetedZonesWrapper}>
              <div className={styles.detailsRowHeader}>
                Targeted Zones ( {selectedZones.length}  )
              </div>
              <div className={helpers.clx(styles.detailsRow, styles.zonesRow)}>
                {
                  visibleZones.map((zone, index) => {
                   
                    return(
                       <div className={helpers.clx(styles.zoneTag, selectedZones.includes(index.toString()) ? styles.selectedZone : "")} 
                          onClick={() => {
                          const zoneIndex = index.toString();
                          if (selectedZones.includes(zoneIndex)) {
                            setSelectedZones(selectedZones.filter(z => z !== zoneIndex));
                          } else {
                            setSelectedZones([...selectedZones, zoneIndex]);
                          }
                      }}>
                      {zone.zone_name}
                      </div>
                      
                     
                    )
               
                  }
                )
              }
            
              </div>
            
            </div>
          
           

            <div className={styles.detailsRow}>
              <TextInput 
                name="Plan Name*" 
                value={formData.planName} 
                onChange={(value) => handleChange("planName", value)} 
                labelStyle="top" 
                placeholder="Enter Plan Name"
              /> 

              {planType === "livestock" ? (
                <TextInput name="# of Rotations/Cycles*"
                  value={livestockInputs.cycles}
                  onChange={(v) => setLivestockInputs(prev => ({ ...prev, cycles: Number(v || 1) }))}
                  labelStyle="top" placeholder="e.g. 4"/>
              ) : <Selector
                name="Target Year*"
                placeholder="Select Year"
                value={formData.planYear || ""}
                onChange={(value) => handleChange("planYear", value)}
                labelStyle="top"
                data={yearsAvailable} // Matches schema enum
              />}
            </div>

            {planType === "land" ? (
              <div className={styles.detailsRow}>
                <Selector 
                  name="Biochar Product*" 
                  value={product} 
                  onChange={handleProductChange}
                  placeholder="Select Product"
                  labelStyle="top" 
                  data={[{name:"PinChar", value:"pinchar"},{name:"PelletChar", value:"pelletchar"}]}
                />
                <div className={styles.detailsRow}/>
              </div>
            ) : (
              <div className={styles.detailsRow}>
                <Selector 
                  name="Biochar Product*" 
                  value={product} 
                  onChange={handleProductChange}
                  placeholder="Select Product"
                  labelStyle="top" 
                  data={[{name:"PinChar", value:"pinchar"},{name:"PelletChar", value:"pelletchar"}]}
                />
                <Selector
                  name="Bedding Type*"
                  value={beddingType}
                  onChange={setBeddingType}
                  labelStyle="top"
                  placeholder="Select Bedding Type"
                  data={BEDDING_OPTIONS}
                />
              </div>
            )}
            
            
            {planType === "livestock" && (
              <div className={styles.detailsRow}>
                
                {/* filler or any other field */}
                <div/>
              </div>
            )}

            <div className={styles.tableContainer}>
              <table className={styles.orderTable}>
                {planType === "land" ? (
                  <>
                    <tr className={styles.orderHeaderRow}>
                      <th>Zone</th>
                      <th>Area (ha)</th>
                      <th>Applied (t/ha)</th>
                      <th>Recommended (t/ha)</th>
                      <th>Targeted (t/ha)</th>
                    </tr>
                    {renderLandRows()}
                  </>
                ) : (
                  <>
                    <tr className={styles.orderHeaderRow}>
                      <th>Zone</th>
                      <th>Bedding Application (t)</th>
                      <th>Bedding Top Up (t)</th>
                      <th>Biochar Inclusion Rate (%)</th>
                      <th>Total Biochar (t)</th>
                    </tr>
                    {renderLivestockRows()}
                  </>
                )}
              </table>
            </div>

            {renderBreakdown()}
            <div className={styles.buttonRow}>
              <Button onPress={() => props.goToScreen("zones")} name={"Cancel"} color="Coal"/>
              <Button onPress={() => handleSubmit()} name={"Save Plan"} color="Leaf"/>
            </div>
          </div>
        </Module>
        {/* <Button onPress={() => setAllPolygons([])}/> */}
        {/* <Module name={"User Details"} spanColumn={10} spanRow={2} height={"100%"}>
          <div/>
        </Module> */}
      </div>
    </div>
  )

}

export default NewPlanScreen;
